import {
  Autocomplete,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  TextField,
  Tooltip,
} from "@mui/material";
import axios from "axios";
import { API_URL } from "const/constants";
import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { ensureProviderAccount, getProviderName, getProviderUniversityId } from "./providerUtils";
import { useAllProviders } from "./useAllProviders";

// Extract first integer from any term string: "T1", "Term-1", "1" → 1
const extractTermNumber = (str) => {
  if (!str) return null;
  const match = String(str).match(/\d+/);
  return match ? parseInt(match[0], 10) : null;
};

export default function SendAccountGroupToProviderModal({
  open,
  onClose,
  group,
  providerList = [],
  refetchProvider,
  onSent,
}) {
  const [useRespectiveProviders, setUseRespectiveProviders] = useState(true);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const fullProviderList = useAllProviders(open);
  const providerOptions = fullProviderList.length ? fullProviderList : providerList;
  const [selectedTermNums, setSelectedTermNums] = useState(new Set());
  const [agentName, setAgentName] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) {
      setUseRespectiveProviders(true);
      setSelectedProvider(null);
      setSelectedTermNums(new Set());
      setAgentName("");
      setSubmitting(false);
    }
  }, [open]);

  const handleSend = async () => {
    if (!group?.id) return;
    if (!useRespectiveProviders && !selectedProvider) {
      toast.warning("Select a provider");
      return;
    }

    setSubmitting(true);
    try {
      const provider = useRespectiveProviders
        ? null
        : await ensureProviderAccount(selectedProvider, refetchProvider);

      await axios.post(`${API_URL}/account-master-groups/send-to-provider`, {
        groupId: group.id,
        useRespectiveProviders,
        accountProviderId: provider?.accountProviderId || null,
        termNumbers: selectedTermNums.size > 0 ? [...selectedTermNums] : null,
        agentName: agentName.trim() || null,
      });
      toast.success("Group sent to provider");
      onSent?.();
      onClose();
    } catch (e) {
      toast.error(
        e?.response?.data?.errorMessage ||
          e?.response?.data?.message ||
          e?.message ||
          "Failed to send group"
      );
    } finally {
      setSubmitting(false);
    }
  };

  // Group raw term strings by normalized integer, track which clients use each raw string
  const termGroups = useMemo(() => {
    // { normNum: { rawStrings: Set<string>, clients: { rawStr: Set<clientName> } } }
    const map = {};
    (group?.members || []).forEach((member) => {
      const clientLabel = member.clientName || `Client #${member.clientId}`;
      (member.availableTerms || []).forEach((raw) => {
        const num = extractTermNumber(raw);
        if (!num) return;
        if (!map[num]) map[num] = { rawStrings: new Set(), clients: {} };
        map[num].rawStrings.add(raw);
        if (!map[num].clients[raw]) map[num].clients[raw] = new Set();
        map[num].clients[raw].add(clientLabel);
      });
    });
    return Object.entries(map)
      .sort(([a], [b]) => Number(a) - Number(b))
      .map(([num, { rawStrings, clients }]) => ({
        num: Number(num),
        rawStrings: [...rawStrings].sort(),
        clients,
      }));
  }, [group]);

  const toggleTerm = (num) => {
    setSelectedTermNums((prev) => {
      const next = new Set(prev);
      next.has(num) ? next.delete(num) : next.add(num);
      return next;
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Send Group to Provider</DialogTitle>
      <DialogContent>
        <div className="space-y-3 mt-1">
          <div className="text-sm text-app-text">
            <span className="font-semibold">{group?.name}</span>
            {group?.members?.length
              ? ` · ${group.members.length} account${group.members.length === 1 ? "" : "s"}`
              : ""}
          </div>

          <FormControlLabel
            control={
              <Checkbox
                checked={useRespectiveProviders}
                onChange={(e) => setUseRespectiveProviders(e.target.checked)}
              />
            }
            label="Use respective provider from each admission"
          />

          {!useRespectiveProviders && (
            <Autocomplete
              size="small"
              options={providerOptions}
              getOptionLabel={(option) => getProviderName(option)}
              isOptionEqualToValue={(option, value) =>
                Number(getProviderUniversityId(option)) === Number(getProviderUniversityId(value))
              }
              value={selectedProvider}
              onChange={(_, value) => setSelectedProvider(value)}
              renderInput={(params) => <TextField {...params} label="Provider" size="small" />}
            />
          )}

          <div>
            <p className="text-xs text-app-text-secondary mb-1">
              Terms to send (leave blank = all) — click to select
            </p>
            {termGroups.length === 0 ? (
              <p className="text-xs text-app-text-muted italic">
                No term data found for group members
              </p>
            ) : (
              <div className="flex flex-wrap gap-1">
                {termGroups.map(({ num, rawStrings, clients }) => {
                  const tooltipContent = (
                    <div className="max-h-48 overflow-y-auto text-xs">
                      <div className="font-semibold mb-1">Term {num}</div>
                      {rawStrings.map((raw) => (
                        <div key={raw} className="mb-1">
                          <span className="font-medium">{raw}:</span> {[...clients[raw]].join(", ")}
                        </div>
                      ))}
                    </div>
                  );
                  return (
                    <Tooltip key={num} title={tooltipContent} arrow placement="top">
                      <Chip
                        label={
                          rawStrings.length > 1
                            ? `T${num} (${rawStrings.join(" / ")})`
                            : rawStrings[0]
                        }
                        size="small"
                        clickable
                        color={selectedTermNums.has(num) ? "primary" : "default"}
                        onClick={() => toggleTerm(num)}
                        sx={{ fontSize: 11 }}
                      />
                    </Tooltip>
                  );
                })}
              </div>
            )}
          </div>

          <TextField
            label="Agent name override (optional)"
            size="small"
            value={agentName}
            onChange={(e) => setAgentName(e.target.value)}
            fullWidth
          />
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSend} disabled={submitting}>
          {submitting ? "Sending..." : "Send"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
